import type {
  ObjectFitType,
  ObjectPositionXType,
  ObjectPositionYType,
} from "@v1/types/object-fit";
import type { AssetLoadingType } from "replo-runtime/shared/asset-loading";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import AssetLibraryPopover from "@v1/components/assets/AssetLibraryPopover";
import ChevronMenuIndicator from "@v1/components/common/designSystem/ChevronMenuIndicator";
import DynamicDataButton from "@v1/components/common/designSystem/DynamicDataButton";
import Input from "@v1/components/common/designSystem/Input";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import Selectable from "@v1/components/designSystem/Selectable";
import { DynamicDataSelector } from "@v1/components/editor/page/DynamicDataSelector";
import ControlGroup from "@v1/components/editor/page/element-editor/components/extras/ControlGroup";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import ModifierLabel from "@v1/components/editor/page/element-editor/components/extras/ModifierLabel";
import InlineAssetSelector from "@v1/components/editor/page/element-editor/components/modifiers/InlineAssetSelector";
import { LengthInputSelector } from "@v1/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import { styleAttributeToEditorData } from "@v1/utils/styleAttribute";
import startCase from "lodash-es/startCase";
import { ImageOff } from "lucide-react";
import { isDynamicDataValue } from "replo-runtime/shared/utils/dynamic-data";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

// Note (Evan, 2024-04-26): Support either passing in { allowsChangingAltText, altTextValue, onChangeAltText, altTextControlLabel }
// to use the default LabeledControl or a altTextComponent to be rendered as a child (composition ftw).
type AltTextValueProps = {
  altTextValue?: string;
  allowsChangingAltText?: boolean;
  onChangeAltText?(value: string): void;
  altTextControlLabel?: string;
  altTextComponent?: undefined;
};

type AltTextComponentProps = {
  altTextValue?: undefined;
  allowsChangingAltText?: undefined;
  onChangeAltText?: undefined;
  altTextControlLabel?: undefined;
  altTextComponent: React.ReactNode;
};

type AltTextProps = AltTextValueProps | AltTextComponentProps;

type AssetPickerProps = {
  assetType: "image" | "video";
  url?: string | ReploMixedStyleValue;
  emptyTitle: string;
  emptyStateButtonLabel: string;
  selectedStateButtonLabel: string;
  objectPositionValue?: {
    x?: string | ReploMixedStyleValue;
    y?: string | ReploMixedStyleValue;
  };
  objectFitValue?: ObjectFitType | ReploMixedStyleValue;
  assetLoadingValue?: AssetLoadingType | ReploMixedStyleValue;
  onChangeObjectFit?(value: ObjectFitType): void;
  onChangeAssetLoading?(value: AssetLoadingType): void;
  onChangeObjectPositionX?(value: ObjectPositionXType): void;
  onChangeObjectPositionY?(value: ObjectPositionYType): void;
  onChangeSource?(value: string, altText?: string | null): void;
  allowsSettingDynamicData?: boolean;
  onClickDynamicDataForUrl?(): void;
  onClickDynamicDataForAltText?(): void;
  onClickDynamicDataForPoster?(): void;
  onPosterInputChange?(value: string): void;
  componentId?: string;
  isUrlDynamicData?: boolean;
  templateValue?: string | ReploMixedStyleValue;
  onClickRemoveDynamicData?(): void;
  onChangeAssetLoadingPoster?(value: string): void;
  onRemoveThumbnail?(): void;
  thumbnailSrc?: string | ReploMixedStyleValue;
} & AltTextProps;

type PreviewProps = {
  url: string;
  objectFitValue?: ObjectFitType;
  objectPositionXValue?: string;
  objectPositionYValue?: string;
} & React.HTMLAttributes<HTMLElement>;

const loadingOptions = [
  { value: "lazy", label: "Lazy: Loads on scroll" },
  { value: "eager", label: "Eager: Loads with page" },
];

const objectFitOptions = [
  { value: "fill", label: "Stretch To Fill" },
  { value: "contain", label: "Contain In Bounds" },
  { value: "cover", label: "Resize To Fill" },
  { value: "none", label: "Don't Resize" },
  { value: "scale-down", label: "Scale Down" },
];

const AssetPicker = ({
  assetType,
  url,
  emptyTitle,
  emptyStateButtonLabel,
  selectedStateButtonLabel,
  objectPositionValue,
  objectFitValue,
  assetLoadingValue,
  onChangeObjectFit,
  onChangeAssetLoading,
  onChangeObjectPositionX,
  onChangeObjectPositionY,
  onChangeSource,
  altTextControlLabel = "Alt Text",
  allowsChangingAltText = false,
  onChangeAltText,
  allowsSettingDynamicData = false,
  onClickDynamicDataForUrl,
  onClickDynamicDataForAltText,
  onClickDynamicDataForPoster,
  altTextValue,
  componentId,
  isUrlDynamicData = false,
  templateValue,
  onClickRemoveDynamicData,
  onChangeAssetLoadingPoster,
  onRemoveThumbnail,
  thumbnailSrc,
  onPosterInputChange,
  altTextComponent,
}: AssetPickerProps) => {
  const [objectPositionXValue, setObjectPositionXValue] = React.useState<
    ObjectPositionXType | ReploMixedStyleValue | undefined
  >(objectPositionValue?.x);
  const [objectPositionYValue, setObjectPositionYValue] = React.useState<
    ObjectPositionYType | ReploMixedStyleValue | undefined
  >(objectPositionValue?.y);

  const applyComponentAction = useApplyComponentAction();

  const [isAssetPickerPopoverOpen, setIsAssetPickerPopoverOpen] =
    React.useState(false);

  function _onSelectX(value: ObjectPositionXType) {
    setObjectPositionXValue(value);
    onChangeObjectPositionX?.(value);
  }

  function _onSelectY(value: ObjectPositionYType) {
    setObjectPositionYValue(value);
    onChangeObjectPositionY?.(value);
  }

  const objectPositionXOptions = [
    {
      id: "left",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      value: "left",
      title: "Left",
      onSelect: () => _onSelectX("left"),
    },
    {
      id: "center",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      value: "center",
      title: "Center",
      onSelect: () => _onSelectX("center"),
    },
    {
      id: "right",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      value: "right",
      title: "Right",
      onSelect: () => _onSelectX("right"),
    },
  ];

  const objectPositionYOptions = [
    {
      id: "top",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      title: "Top",
      onSelect: () => _onSelectY("top"),
    },
    {
      id: "center",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      title: "Center",
      onSelect: () => _onSelectY("center"),
    },
    {
      id: "bottom",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      title: "Bottom",
      onSelect: () => _onSelectY("bottom"),
    },
  ];

  const onSelectAsset = (isImagePreview: boolean) => {
    if (isUrlDynamicData && isImagePreview) {
      onClickDynamicDataForUrl?.();
    } else if (!isUrlDynamicData) {
      setIsAssetPickerPopoverOpen((val) => !val);
    }
  };

  const altTextInputProps = useOverridableInput({
    value: altTextValue ?? "",
    onValueChange: onChangeAltText,
  });

  function getObjectPositionValue(
    value: string | ReploMixedStyleValue | undefined,
  ) {
    if (isMixedStyleValue(value)) {
      return value;
    }
    if (value?.includes("px") || value?.includes("%")) {
      return value;
    }
    return startCase(value);
  }

  function getThumbnailSrc(value: string | ReploMixedStyleValue | undefined) {
    if (!value) {
      return undefined;
    }
    if (isMixedStyleValue(value)) {
      return value;
    } else {
      return {
        type: "image" as const,
        src: value,
      };
    }
  }

  return (
    <div className="flex flex-col items-stretch gap-2">
      <div
        onClick={() => onSelectAsset(true)}
        className="mb-2 flex h-[150px] cursor-pointer flex-col items-stretch"
      >
        {url &&
          !isMixedStyleValue(url) &&
          !isMixedStyleValue(objectFitValue) &&
          (isUrlDynamicData ? (
            <>
              {assetType === "image" ? (
                <DynamicDataSelector
                  side="left"
                  sideOffset={36}
                  trigger={
                    <ImagePreview url={url} objectFitValue={objectFitValue} />
                  }
                  targetType="image"
                  onChange={(newValue) => onChangeSource?.(newValue)}
                  onRemove={onClickRemoveDynamicData}
                  layoutClassName="h-full"
                />
              ) : (
                <DynamicDataSelector
                  side="left"
                  sideOffset={36}
                  trigger={
                    <VideoPreview url={url} objectFitValue={objectFitValue} />
                  }
                  targetType="text/url"
                  onChange={(newValue) => onChangeSource?.(newValue)}
                  onRemove={onClickRemoveDynamicData}
                  layoutClassName="h-full"
                />
              )}
            </>
          ) : (
            <>
              {assetType === "image" ? (
                <ImagePreview url={url} objectFitValue={objectFitValue} />
              ) : (
                <VideoPreview url={url} objectFitValue={objectFitValue} />
              )}
            </>
          ))}

        {!url && <EmptyUrlPreview emptyTitle={emptyTitle} />}
        {isMixedStyleValue(url) && <MixedPreview />}
      </div>
      {isUrlDynamicData && !isMixedStyleValue(templateValue) ? (
        <DynamicDataSelector
          side="left"
          initialPath={templateValue?.split(".") ?? []}
          sideOffset={16}
          trigger={
            <DynamicDataValueIndicator
              type="image"
              templateValue={templateValue ?? "Dynamic Image"}
              componentId={componentId}
              onRemove={onClickRemoveDynamicData}
            />
          }
          targetType="image"
          onChange={(newValue) => onChangeSource?.(newValue)}
          onRemove={onClickRemoveDynamicData}
        />
      ) : (
        <div className="flex h-6 gap-2">
          <AssetLibraryPopover
            isOpen={isAssetPickerPopoverOpen}
            setIsOpen={setIsAssetPickerPopoverOpen}
            onChangeSource={onChangeSource} // here
            assetTypes={[assetType]}
            // TODO (Gabe 2025-02-24): Why do we provide an anchor here? really
            // we should just be able to provide the trigger
            anchor={
              <DeprecatedButton
                variant="primary"
                onClick={() => onSelectAsset(false)}
                layoutClassName="grow"
                disabled={isUrlDynamicData}
                isActive={isAssetPickerPopoverOpen}
              >
                {url ? selectedStateButtonLabel : emptyStateButtonLabel}
              </DeprecatedButton>
            }
          />
          {allowsSettingDynamicData && (
            <DynamicDataSelector
              side="left"
              sideOffset={220}
              trigger={<DynamicDataButton />}
              targetType="image"
              onChange={(newValue) => onChangeSource?.(newValue)}
              onRemove={onClickRemoveDynamicData}
            />
          )}
        </div>
      )}
      {(Boolean(onChangeObjectFit) || allowsChangingAltText) && (
        <>
          {onChangeObjectFit ? (
            <ControlGroup label="Object Fit">
              <Selectable
                layoutClassName="w-full"
                triggerLayoutClassName="w-full"
                placeholder="Don't Resize"
                options={objectFitOptions}
                value={objectFitValue}
                defaultValue={styleAttributeToEditorData.objectFit.defaultValue}
                onSelect={(value: ObjectFitType) => onChangeObjectFit(value)}
                isDisabled={!url}
              />
            </ControlGroup>
          ) : null}
          {onChangeAssetLoading ? (
            <ControlGroup label="Loading">
              <Selectable
                popoverLayoutClassName="w-48"
                placeholder="Loading"
                dropdownAlign="end"
                options={loadingOptions}
                value={assetLoadingValue}
                defaultValue="eager"
                onSelect={(value: AssetLoadingType) => {
                  return onChangeAssetLoading(value);
                }}
              />
            </ControlGroup>
          ) : null}
          {Boolean(onChangeObjectPositionX) &&
          Boolean(onChangeObjectPositionY) ? (
            <>
              <LengthInputSelector
                value={getObjectPositionValue(objectPositionXValue)}
                label={<ModifierLabel label="X Position" />}
                onChange={_onSelectX}
                endEnhancer={() => (
                  <ChevronMenuIndicator items={objectPositionXOptions} />
                )}
                draggingType="vertical"
                dragTrigger="label"
                field="PositionX"
                placeholder="center"
                metrics={["px", "%", "inherit"]}
                allowedNonUnitValues={["left", "center", "right"]}
                isDisabled={!url}
                previewProperty="objectPosition"
                previewSubProperty="objectPositionX"
                resetValue="center"
              />
              <LengthInputSelector
                value={getObjectPositionValue(objectPositionYValue)}
                label={<ModifierLabel label="Y Position" />}
                onChange={_onSelectY}
                endEnhancer={() => (
                  <ChevronMenuIndicator items={objectPositionYOptions} />
                )}
                draggingType="vertical"
                dragTrigger="label"
                field="PositionY"
                placeholder="center"
                metrics={["px", "%", "inherit"]}
                allowedNonUnitValues={["top", "center", "bottom"]}
                isDisabled={!url}
                previewProperty="objectPosition"
                previewSubProperty="objectPositionY"
                resetValue="center"
              />
            </>
          ) : null}
          {onChangeAssetLoadingPoster ? (
            <ControlGroup label="Thumbnail">
              <div className="align-start flex w-full flex-col items-center gap-2">
                {!isMixedStyleValue(thumbnailSrc) &&
                isDynamicDataValue(thumbnailSrc) ? (
                  <DynamicDataSelector
                    side="left"
                    sideOffset={90}
                    triggerClassName="w-full"
                    initialPath={thumbnailSrc?.split(".") ?? []}
                    trigger={
                      <DynamicDataValueIndicator
                        type="image"
                        className="w-full"
                        templateValue={thumbnailSrc ?? null}
                        onRemove={() => onRemoveThumbnail?.()}
                        componentId={componentId}
                      />
                    }
                    targetType="image"
                    onChange={(value) => {
                      return applyComponentAction({
                        type: "setProps",
                        value: { poster: value },
                      });
                    }}
                  />
                ) : (
                  <InlineAssetSelector
                    size="sm"
                    emptyTitle="Select Thumbnail"
                    onClickDynamicData={onClickDynamicDataForPoster}
                    allowsDynamicData
                    isImageOnly
                    onRemoveAsset={() => onRemoveThumbnail?.()}
                    allowRemoveAsset={Boolean(onRemoveThumbnail)}
                    asset={getThumbnailSrc(thumbnailSrc)}
                    onChange={onPosterInputChange}
                  />
                )}
              </div>
            </ControlGroup>
          ) : null}
          {altTextComponent ??
            (allowsChangingAltText && (
              <ControlGroup
                label={altTextControlLabel}
                className={twMerge(
                  "flex",
                  !isDynamicDataValue(altTextValue) && "items-start",
                )}
              >
                {isDynamicDataValue(altTextValue) && (
                  <DynamicDataValueIndicator
                    type="text"
                    templateValue={altTextValue ?? ""}
                    onClick={() => onClickDynamicDataForAltText?.()}
                    onRemove={() => onChangeAltText?.("")}
                    componentId={componentId}
                  />
                )}
                {!isDynamicDataValue(altTextValue) && (
                  <div className="flex gap-2">
                    <div className="flex-grow">
                      <Input {...altTextInputProps} />
                    </div>
                    {allowsSettingDynamicData && (
                      <DynamicDataButton
                        onClick={() => onClickDynamicDataForAltText?.()}
                      />
                    )}
                  </div>
                )}
              </ControlGroup>
            ))}
        </>
      )}
    </div>
  );
};

const ImagePreview = React.forwardRef<HTMLElement, PreviewProps>(
  ({ url, objectFitValue, ...props }, ref) => {
    const [isBroken, setIsBroken] = React.useState(false);

    React.useEffect(() => {
      if (url) {
        setIsBroken(false);
      }
    }, [url]);

    if (isBroken) {
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          className="bg-surface text-muted flex h-full flex-col items-center justify-center gap-1.5 rounded-sm text-xs font-medium"
          {...props}
        >
          <ImageOff size={24} />
          <span>Preview unavailable</span>
        </div>
      );
    }

    return (
      <img
        ref={ref as React.Ref<HTMLImageElement>}
        src={url}
        onError={() => setIsBroken(true)}
        alt="image"
        className="shadow-2xs h-full w-full rounded-sm object-cover object-center"
        style={{
          objectFit: objectFitValue,
        }}
        {...props}
      />
    );
  },
);

ImagePreview.displayName = "ImagePreview";

const VideoPreview = React.forwardRef<HTMLVideoElement, PreviewProps>(
  ({ url, objectFitValue, ...props }, ref) => {
    return (
      <video
        ref={ref}
        className="h-full rounded-sm object-cover object-center"
        style={{
          objectFit: objectFitValue,
        }}
        src={url}
        {...props}
      />
    );
  },
);

VideoPreview.displayName = "VideoPreview";

const EmptyUrlPreview: React.FC<{ emptyTitle: string }> = ({ emptyTitle }) => {
  return (
    <div className="bg-surface flex flex-1 items-center justify-center rounded-sm">
      <span className="text-muted text-xs">{emptyTitle}</span>
    </div>
  );
};

const MixedPreview: React.FC = () => {
  return (
    <div className="bg-sky-soft flex h-full items-center justify-center gap-2 rounded-sm">
      <Badge type="dynamicData" />
      <span className="text-primary text-xs">Mixed</span>
    </div>
  );
};

export default AssetPicker;
